import React, { useState, useEffect, useContext } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import array from "lodash";
import SEO from "../components/seo";
import "react-dates/initialize";
import moment from "moment";
import "react-dates/lib/css/_datepicker.css";
import { SingleDatePicker } from "react-dates";
import { DialogActions } from "@material-ui/core";
import client from "../utils/graphql";
import { gql } from "apollo-boost";
import { useQuery } from "@apollo/react-hooks";
import Navbar from "../components/Navbar";
import UserContext from "../contexts/UserContext";
import { navigate } from "gatsby";

const ADD_BOOKING_ITEM = gql`
  mutation addBookingItem($Start: DateTime, $End: DateTime, $Studio: ID) {
    createBookingItem(
      input: { data: { Start: $Start, End: $End, BookedStudio: $Studio } }
    ) {
      bookingItem {
        id
      }
    }
  }
`;

const ADD_BOOKING = gql`
  mutation createBooking($BookingUser: ID, $booking_items: [ID]) {
    createBooking(
      input: {
        data: { BookingUser: $BookingUser, booking_items: $booking_items }
      }
    ) {
      booking {
        id
      }
    }
  }
`;

const GET_BOOKINGS = gql`
  query GetBookings($Studio: ID, $Start: DateTime, $End: DateTime) {
    bookingItems(
      where: { BookedStudio: $Studio, Start_gte: $Start, End_lte: $End }
    ) {
      id
      Start
      End
    }
  }
`;

const FIRST_APPOINTMENT = 8;
const LAST_APPOINTMENT = 22;
moment.locale("fr");

const ChoiceButton: React.FC<{
  number: number;
  selected: boolean;
  addToSelection: (hour: number) => void;
  removeFromSelection: (hour: number) => void;
}> = ({ number, addToSelection, removeFromSelection, selected }) => {
  const [selectedState, setSelected] = useState(selected);

  return (
    <div
      onClick={() => {
        selectedState ? removeFromSelection(number) : addToSelection(number);
        setSelected(previous => !previous);
      }}
      className={`m-2 p-2 rounded-lg focus:outline-none ${
        selectedState ? "bg-blue-800 text-white" : "bg-white-900 text-black"
      } rounded border border-blue-500`}
    >
      {number}h à {number + 1}h
    </div>
  );
};

const BookingPage: React.FC = () => {
  const [date, setDate] = useState<moment.Moment | null>(null);
  const [focused, setFocused] = useState<boolean | null>(false);
  const [hourDialog, setHourDialog] = useState(false);
  const [opening, setOpening] = useState(true);
  const [selection, setSelection] = useState(new Array<moment.Moment>());
  const { isSignedIn, user } = useContext(UserContext);

  const { loading, error, data, refetch } = useQuery(GET_BOOKINGS, {
    variables: {
      Studio: "5e94e584c46daf00177f0c61",
      Start: date
        ?.clone()
        .hour(FIRST_APPOINTMENT)
        .utc(),
      End: date
        ?.clone()
        .hour(LAST_APPOINTMENT)
        .utc(),
    },
    client,
  });

  useEffect(() => {
    if (!isSignedIn()) {
      navigate("signin");
    }
  }, []);

  useEffect(() => {
    if (!opening && date) {
      setHourDialog(true);
    }
  }, [date]);

  function addDateToSelection(hour: number) {
    if (date) {
      const dateToAdd = date.clone();
      dateToAdd?.hour(hour);
      selection;
      setSelection(prev => array.concat(prev, dateToAdd));
    }
  }

  function removeDateFromSelection(hour: number) {
    if (date) {
      const dateToremove = date?.clone();
      dateToremove?.hour(hour);
      setSelection(prev =>
        array.filter(prev, date => !date.isSame(dateToremove, "hour"))
      );
    }
  }

  function constructChoices() {
    if (!loading && data) {
      const hoursFromQuery = data["bookingItems"] as {
        id: number;
        Start: string;
        End: string;
      }[];
      const hoursBooked = hoursFromQuery.map(data => moment(data.Start).hour());
      const hoursAll = array.range(FIRST_APPOINTMENT, LAST_APPOINTMENT);
      const hours = array.difference(hoursAll, hoursBooked);

      if (hours.length > 0) {
        const choices = hours.map((hour, index) => {
          if (date) {
            const dateToTest = date.clone();
            dateToTest.hour(hour);
            const exists = array.findIndex(selection, date =>
              date.isSame(dateToTest, "hour")
            );
            return (
              <ChoiceButton
                key={index}
                selected={exists != -1}
                number={hour}
                addToSelection={addDateToSelection}
                removeFromSelection={removeDateFromSelection}
              />
            );
          }
        });
        return <div className="flex flex-wrap">{choices}</div>;
      } else {
        return (
          <div className="flex flex-wrap">Aucune plage horaire disponible</div>
        );
      }
    } else {
      return <div> Loading</div>;
    }
  }

  function displayChoices() {
    const choicesLi = selection.map((date: moment.Moment, index: number) => (
      <li key={index}>{date.format("dddd, MMMM D YYYY, hA")}</li>
    ));
    return <ul>{choicesLi}</ul>;
  }

  async function commitTransaction() {
    if (selection.length === 0) return;

    const createdBookingItems = selection.map(async date => {
      const addBookingItemTransaction = await client.mutate({
        mutation: ADD_BOOKING_ITEM,
        variables: {
          Start: date.utc(),
          End: date
            .clone()
            .add(1, "hours")
            .utc(),
          Studio: "5e94e584c46daf00177f0c61",
        },
      });
      const createdId =
        addBookingItemTransaction.data["createBookingItem"]["bookingItem"][
          "id"
        ];
      console.log(createdId);
      return await createdId;
    });

    const resolvedIds = await Promise.all(createdBookingItems);
    await client.mutate({
      mutation: ADD_BOOKING,
      variables: {
        BookingUser: user.id,
        booking_items: resolvedIds,
      },
    });

    setSelection([]);
    await refetch();
    setDate(null);
  }

  return (
    <div>
      <SEO title="Reservation" />
      <Navbar />
      <div className="p-4">
        <div>Choisir une date:</div>
        <SingleDatePicker
          date={date} // momentPropTypes.momentObj or null
          onDateChange={date => {
            setOpening(false);
            setDate(date);
          }} // PropTypes.func.isRequired
          focused={focused} // PropTypes.bool
          onFocusChange={({ focused }) => setFocused(focused)} // PropTypes.func.isRequired
          numberOfMonths={1}
          id="your_unique_id" // PropTypes.string.isRequired,
        />
        <div className="mt-4">
          <div>Plages horaires choisies:</div>
          <div>{displayChoices()}</div>
        </div>
        <div className="mt-4">
          <button
            onClick={commitTransaction}
            className="border rounded p-4 mt-4 bg-blue-800 text-white"
          >
            Confirmer
          </button>
        </div>
      </div>
      <Dialog open={hourDialog} onClose={() => setHourDialog(false)}>
        <DialogContent>
          Choisir une heure:
          <div className="mt-4">{constructChoices()}</div>
        </DialogContent>
        <DialogActions>
          <button
            onClick={() => setHourDialog(false)}
            className="m-2 p-2 border rounded bg-blue-800 text-white focus:outline-none"
          >
            Valider
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BookingPage;
